import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cx } from '@chakra-ui/utils';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Box,
  ChakraProvider,
} from '@chakra-ui/react';
import { FeatureFlagProvider } from '@cloudbeds/feature-flag-manager-payments';

import { CSSReset } from '../CSSReset';
import { useMappingService } from '../../queries/useMappingService';
import { FailedToLoad } from '../ui-elements/FailedToLoad';
import { useDynamicRefs } from '../../utils/dynRefs';
import { objectKeys } from '../../utils/objectKeys';
import { generateStripeTheme, generateMainTheme } from '../../utils/theme';
import { generatePaymentMethodsConfiguration } from '../../utils/paymentMethods';
import { usePaymentMethodStore, useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';
import { ProcessorProvider } from '../providers/ProcessorProvider';
import { AccordionHeader } from '../ui-elements/AccordionHeader';
import { Empty } from '../ui-elements/Empty';
import { Slot } from '../ui-elements/Slot';
import { PaymentMethodRegistry as registry } from '../PaymentMethodRegistry';
import { usePaymentElementSettingsV2 } from '../../queries/usePaymentElementSettingsV2';
import { createLogLocal } from '../../utils/log-local';
import { AppConfig } from '../../config';
import { FeatureFlag } from '../../utils/feature-flags/FeatureFlag';

import { defaultTheme } from './default-theme';

import type {
  CaptureModeType,
  EnvType,
  LabelsType,
  Settings,
} from '../../@types';
import type { PaymentElementPropTypes } from './payment.types';

const log = createLogLocal('PaymentElement.tsx');

/**
 * Apply to the topmost element of the entire payment SDK.
 * Allows the SDK to be styled independently of the host application if
 * `scopedCss` is set to `true`.
 */
const psdkScopeClass = 'payment-sdk-scope';

/**
 * Main exported element from @cloudbeds/payment-sdk.
 * Use in React UI tree to embed the payment SDK in a web application.
 *
 * Provides all payment functionality to the page it's embedded in, including
 * the payment type selection accordion and forms for each payment method.
 *
 * @param {object} props.theme All theme data required to style the form.
 * @param {string} props.propertyId Int identifying property the payment is for.
 *                                  Example: 284451
 * @param {string} props.lang Code for currently active langauge. Default: "en"
 */
export const PaymentElement = (props: PaymentElementPropTypes) => {
  const {
    apiKey,
    propertyId,
    lang,
    onError,
    onLoadStart,
    onLoadEnd,
    onChange,
    amount,
    currency = 'usd',
    capture = 'auto',
    labels,
    allowedPaymentMethods,
    env = 'prod',
    scopedCss = true,
    onRender,
    slots,
    redirectAction,
    paymentMethodFilters,
  } = props;

  /**
   * Reason for the obtuse method of setting default theme: when rendering
   * PaymentElement as a web component, the theme prop defaults to an empty
   * object, instead of undefined (as with a React component).
   */
  const theme =
    !props.theme ||
    (typeof props.theme === 'object' && Object.keys(props.theme).length === 0)
      ? defaultTheme
      : props.theme;

  const mappingServiceUrls = useMappingService(+propertyId, env as EnvType);

  const [settings, isSettingsLoading, settingsErrors]: [
    Settings | null,
    boolean,
    string | null,
  ] = usePaymentElementSettingsV2(
    apiKey,
    +propertyId,
    paymentMethodFilters,
    mappingServiceUrls?.baseUrl,
  );

  const [noPaymentMethodError, setNoPaymentMethodError] =
    useState<boolean>(false);
  const [accordionIndex, setAccordionIndex] = useState(-1);

  const [t] = useTranslations(lang);

  const {
    setStripeTheme,
    setLabels,
    setLang,
    setApiKey,
    setPropertyId,
    setEnv,
    setRedirectAction,
  } = useCommonOptionsStore();

  const [paymentMethod, setPaymentMethod] = usePaymentMethodStore(state => [
    state.paymentMethod,
    state.setPaymentMethod,
  ]);

  /**
   * Retrieve list of payment methods available for the property.
   */
  const paymentMethods = useMemo(
    () =>
      settings
        ? generatePaymentMethodsConfiguration(
            settings,
            amount,
            capture as CaptureModeType,
          )
        : {},
    [settings, capture, amount],
  );

  const mainTheme = useMemo(() => generateMainTheme(theme), [theme]);
  const stripeTheme = useMemo(
    () => generateStripeTheme(theme.components, theme.foundations),
    [theme.components, theme.foundations],
  );

  /**
   * Define and read refs for payment method forms.
   * Used to access payment method forms from the upper scope.
   */
  const [definePaymentFormRef, readPaymentFormRef] = useDynamicRefs();

  /**
   * List of payment methods (from registry) supported by the host application.
   *
   * Doesn't get this directly from props, to avoid breaking the correct order
   * of payment methods on the form.
   */
  const enabledPaymentMethods = React.useMemo(() => {
    const methods = paymentMethods ? Object.keys(paymentMethods) : [];
    return methods.length
      ? objectKeys(registry)
          .filter(payMethod => methods.includes(payMethod))
          .filter(payMethod =>
            allowedPaymentMethods
              ? allowedPaymentMethods.includes(payMethod)
              : true,
          )
          // Filter out payment methods that require an amount if no amount is provided
          .filter(payMethod => !registry[payMethod]?.hasAmount || !!amount)
      : [];
  }, [paymentMethods, amount, allowedPaymentMethods]);

  /**
   * Triggered when user clicks the accordion button for a payment method.
   * Displays the form for the selected payment method and hides the others.
   * @param {number} expandedIndex Index of the payment method selected.
   */
  const onPaymentMethodChange = React.useCallback(
    (expandedIndex: number) => {
      log(
        'onPaymentMethodChange',
        'enabledPaymentMethods',
        enabledPaymentMethods,
      );
      if (onLoadStart) onLoadStart();
      const method = enabledPaymentMethods[expandedIndex];
      setAccordionIndex(expandedIndex);
      setPaymentMethod(method);
      setNoPaymentMethodError(false);
      if (onChange) onChange({ name: 'payment_method', value: method });
      if (onLoadEnd) onLoadEnd();
    },
    [enabledPaymentMethods, onLoadEnd, onLoadStart, setPaymentMethod, onChange],
  );

  /**
   * Set theme for Stripe iFrame if any are present
   */
  React.useEffect(() => {
    if (stripeTheme) setStripeTheme(stripeTheme);
  }, [setStripeTheme, stripeTheme]);

  /**
   * Set custom labels if any are present
   */
  React.useEffect(() => {
    if (labels) setLabels(labels as LabelsType);
  }, [setLabels, labels]);

  /**
   * Set property for secure communications
   */
  React.useEffect(() => {
    if (propertyId) setPropertyId(+propertyId);
  }, [setPropertyId, propertyId]);

  /**
   * Set API key for secure communications
   */
  React.useEffect(() => {
    if (apiKey) setApiKey(apiKey);
  }, [setApiKey, apiKey]);

  React.useEffect(() => {
    if (env) setEnv(env as EnvType);
  }, [setEnv, env]);

  /**
   * Set translations for selected locale
   */
  React.useEffect(() => {
    if (lang) setLang(lang);
  }, [lang, setLang]);

  /**
   * Set behaviour for redirection after payment SDK submits the payment.
   */
  React.useEffect(() => {
    if (redirectAction) setRedirectAction(redirectAction);
    else setRedirectAction({ type: 'navigate' });
  }, [redirectAction, setRedirectAction]);

  /**
   * Set payment method as chosen if only one payment method is available.
   */
  React.useEffect(() => {
    if (enabledPaymentMethods.length === 1 && accordionIndex !== 0) {
      onPaymentMethodChange(0);
    }
  }, [onPaymentMethodChange, enabledPaymentMethods, accordionIndex]);

  /**
   * Handle error(s) when loading configuration for the property
   */
  React.useEffect(() => {
    if (onError && settingsErrors) onError(settingsErrors);
    if (settingsErrors) {
      console.error(`Can not fetch settings: ${settingsErrors}`);
    }
  }, [onError, settingsErrors]);

  /**
   * Submit the payment, using data from the payment form.
   * Triggered by a child form component (e.g. GenericCardForm), or exposed
   * to a parent component to allow triggering outside the Payment SDK.
   */
  const submit = useCallback(
    async (payMethod?: string) => {
      log('submit', 'paymentMethod', paymentMethod);
      log('submit', 'payMethod', payMethod);
      const paymentMethodSelected = paymentMethod || payMethod;

      const formRef = readPaymentFormRef(paymentMethodSelected);
      log('submit', 'formRef', formRef);

      if (!paymentMethodSelected || !formRef) {
        setNoPaymentMethodError(true);
        return false;
      }

      const submitFunc = await (formRef.current as any)?.submit;
      log('submit', 'submitFunc', submitFunc);
      if (submitFunc) return submitFunc();

      return true;
    },
    [paymentMethod, readPaymentFormRef],
  );

  /**
   * Validate the payment form data. Required before submit.
   */
  const validate = useCallback(
    async (payMethod?: string) => {
      log('validate', 'paymentMethod', paymentMethod);
      log('validate', 'payMethod', payMethod);
      const paymentMethodSelected = paymentMethod || payMethod;

      const formRef = readPaymentFormRef(paymentMethodSelected);
      log('validate', 'formRef', formRef);

      if (!paymentMethodSelected || !formRef) {
        setNoPaymentMethodError(true);
        return false;
      }

      const validateFunc = await (formRef.current as any)?.validate;
      log('validate', 'validateFunc', validateFunc);
      if (validateFunc) return validateFunc();

      return true;
    },
    [paymentMethod, readPaymentFormRef],
  );

  useEffect(() => {
    /**
     * Expose methods to the parent component to let the host application work
     * with the form.
     */
    if (onRender) onRender({ submit, validate });
  }, [onRender, props.onRender, submit, validate]);

  /*
   * Apply global additional CSS reset file if `scopedCss` is set to false.
   * Otherwise, the SDK will use a scoped CSS reset that applies only to the
   * PaymentElement component and its children.
   */
  useEffect(() => {
    if (!scopedCss) import('../../assets/css/reset.css');
  }, [scopedCss]);

  log('PaymentElement', 'settings', settings, '\nprops:', props);

  /**
   * Conglomerate wrapper for ChakraProvider & FeatureFlagProvider.
   */
  const PayElConfigProvider = useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <FeatureFlagProvider
        flagEnum={FeatureFlag}
        apiKey={AppConfig.launchDarklyKey}
        propertyId={propertyId}
      >
        <ChakraProvider
          theme={mainTheme}
          resetCSS={!scopedCss}
          disableGlobalStyle={scopedCss}
        >
          {children}
        </ChakraProvider>
      </FeatureFlagProvider>
    ),
    [mainTheme, propertyId, scopedCss],
  );

  /**
   * Show no component if settings are being loaded or null,
   * Show error stub is settings can't be loaded.
   */
  if (isSettingsLoading) return null;

  if (settingsErrors) {
    return (
      <PayElConfigProvider>
        <FailedToLoad />
      </PayElConfigProvider>
    );
  }
  if (!settings) return null;

  const AccordionComponent =
    enabledPaymentMethods.length === 1 ? Empty : Accordion;

  return (
    <PayElConfigProvider>
      {scopedCss && <CSSReset scope={`.${psdkScopeClass}`} />}
      <div className={psdkScopeClass}>
        {/* Render the "Select your preferred payment method" mini-title */}
        {enabledPaymentMethods.length > 1 && (
          <Box
            mb={3}
            fontSize="sm"
            color={noPaymentMethodError ? 'error.500' : ''}
          >
            <>{t('selectPaymentMethod')}</>
          </Box>
        )}

        {/*
         * Render payment methods as selectable accordion rows if more than one
         * is enabled. Otherwise, render the only payment method available.
         */}
        <AccordionComponent
          onChange={onPaymentMethodChange}
          className={cx('TabsContainer')}
          index={accordionIndex}
        >
          {enabledPaymentMethods.map((payMethod, idx) => {
            const paymentMethodObj = registry[payMethod]!;
            /** Form component for the currently selected payment method */
            const PaymentFormComponent = paymentMethodObj.PaymentFormComponent;
            /** Icon component for the currently payment method */
            const Icon = paymentMethodObj.Icon;
            /** Name of selected payment method e.g. "Card", "Paypal" */
            const title = paymentMethodObj.title;

            /** Name of payment processor to use (e.g. 'stripe') */
            const processor =
              settings?.paymentMethodConfigurations[payMethod]!.processor;

            const tokenization =
              settings?.paymentMethodConfigurations[payMethod].tokenization;

            let mode: 'live' | 'test' = 'live';
            if (processor) {
              mode = settings?.mode[processor as any];
            } else if (tokenization?.processor) {
              mode = tokenization[tokenization?.processor]?.mode;
            }

            /**
             * Contains form and config for currently selected payment method.
             *
             * Includes what's displayed in a selected accordion row,
             * or the entire form if only one payment method is available.
             */
            const CurrentPaymentMethodForm = (
              <ProcessorProvider
                key={idx}
                method={payMethod}
                methodConfig={settings?.paymentMethodConfigurations[payMethod]}
                billingConfig={settings?.billingAddressFields}
                processors={settings?.processors}
                options={{ amount, currency }}
              >
                <Slot position="top" slot={slots?.[payMethod]} />
                <PaymentFormComponent
                  payMethod={payMethod}
                  processor={processor}
                  {...props}
                  {...paymentMethods[payMethod]}
                  paymentMethodConfig={
                    settings?.paymentMethodConfigurations[payMethod]
                  }
                  mode={mode}
                  ref={
                    definePaymentFormRef(payMethod) as React.LegacyRef<{
                      submit: () => void;
                      validate: () => Promise<boolean>;
                    }>
                  }
                />
                <Slot position="bottom" slot={slots?.[payMethod]} />
              </ProcessorProvider>
            );

            /**
             * CASE 1: ONLY 1 PAYMENT METHOD AVAILABLE
             * Render:
             *   - Form for selected payment method
             *   - No accordion row
             */
            if (enabledPaymentMethods.length === 1) {
              return CurrentPaymentMethodForm;
            }

            /**
             * CASE 2: 2 OR MORE PAYMENT METHODS AVAILABLE
             * Render:
             *   - Accordion rows for each payment method
             *   - Form for selected payment method under correct accordion row
             */
            return (
              <AccordionItem className={cx('TabContainer')} key={idx}>
                {({ isExpanded }) => (
                  <>
                    <AccordionHeader
                      name={payMethod}
                      title={t(title)}
                      isExpanded={isExpanded}
                    >
                      <Icon />
                    </AccordionHeader>
                    <AccordionPanel pb={4}>
                      {paymentMethod === payMethod && CurrentPaymentMethodForm}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            );
          })}
        </AccordionComponent>
      </div>
    </PayElConfigProvider>
  );
};
